import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../../contexts/MessageContext';
import { employeeAPI, employerAPI, adminAPI } from '../../services/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import EmployeeProfile from '../employee/Profile';
import EmployerProfile from '../employer/Profile';
import EmployerMessages from '../employer/Messages';
import EmployerSearch from '../employer/Search';
import {
  User,
  Building2,
  MessageSquare,
  Search,
  Zap,
  LogOut,
  ChevronRight,
  LayoutDashboard,
  FileText,
  Users,
  TrendingUp,
  Award,
  Clock,
  Bell,
  Settings,
  Shield,
  Briefcase,
  Star,
  Activity,
  Target,
  Loader2
} from 'lucide-react';

const FounderDashboard = () => {
  const { user, logout } = useAuth();
  const { unreadCount } = useMessages();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [employerProfile, setEmployerProfile] = useState(null);
  const [vettScore, setVettScore] = useState(null);
  const [employeeRecords, setEmployeeRecords] = useState([]);
  const [pipelineStats, setPipelineStats] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);

  useEffect(() => {
    loadFounderData();
  }, []);

  const loadFounderData = async () => {
    setLoading(true);
    try {
      // Load employee profile data (silently handle 403/404 - founder may not have profile yet)
      try {
        const empProfileRes = await employeeAPI.getProfile();
        setEmployeeProfile(empProfileRes.data);
      } catch (e) {
        // Silently ignore - founder may not have employee profile yet
      }

      // Load VETT score
      try {
        const scoreRes = await employeeAPI.getVettScore();
        setVettScore(scoreRes.data);
      } catch (e) {
        // Silently ignore - no VETT score yet
      }

      // Load employee records
      try {
        const recordsRes = await employeeAPI.getRecords();
        setEmployeeRecords(recordsRes.data.records || []);
      } catch (e) {
        // Silently ignore - no records yet
      }

      // Load employer profile if exists
      try {
        const emplerProfileRes = await employerAPI.getProfile();
        setEmployerProfile(emplerProfileRes.data);
      } catch (e) {
        // Silently ignore - founder may not have employer profile yet
      }

      // Load admin platform stats
      try {
        const statsRes = await adminAPI.getStats();
        setPlatformStats(statsRes.data);
      } catch (e) {
        // Silently ignore - stats may not load
      }

    } catch (error) {
      // Silently ignore any outer errors
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Sidebar navigation items
  const navItems = [
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/founder/dashboard',
      exact: true
    },
    {
      icon: User,
      label: 'My Profile',
      path: '/founder/profile',
      description: 'Employee Profile Editor'
    },
    {
      icon: Building2,
      label: 'My Company',
      path: '/founder/company',
      description: 'Employer Profile Editor'
    },
    {
      icon: MessageSquare,
      label: 'Messenger',
      path: '/founder/messages',
      badge: unreadCount > 0 ? unreadCount : null
    },
    {
      icon: Search,
      label: 'Talent Search',
      path: '/founder/search',
      description: 'Find candidates'
    },
    {
      divider: true
    },
    {
      icon: Zap,
      label: 'Command Center',
      path: '/admin/command-center',
      accent: true,
      description: 'Operations Suite'
    }
  ];

  // Check if we're on a sub-route (not the main dashboard)
  const isSubRoute = location.pathname !== '/founder/dashboard';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F8FF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B5AFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F8FF] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#FFFFFF] border-r border-[#E8EDF5] flex flex-col fixed h-full z-40">
        {/* Logo/Header */}
        <div className="p-4 border-b border-[#E8EDF5]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1B5AFF] to-[#00C9A7] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-[#0A0F1E] font-bold text-lg">VETT</h1>
              <p className="text-[#5B6472] text-xs">Founder Mode</p>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-[#E8EDF5]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold">
              {user?.email?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#0A0F1E] text-sm font-medium truncate">{user?.email}</p>
              <div className="flex items-center gap-1">
                <Badge className="bg-[#1B5AFF]/20 text-[#1B5AFF] border-0 text-[10px]">
                  FOUNDER
                </Badge>
                <Badge className="bg-purple-500/20 text-purple-400 border-0 text-[10px]">
                  SUPERADMIN
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto">
          <div className="space-y-1">
            {navItems.map((item, index) => {
              if (item.divider) {
                return <div key={index} className="my-3 border-t border-[#E8EDF5]" />;
              }

              const isActive = item.exact 
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group ${
                    isActive
                      ? item.accent 
                        ? 'bg-gradient-to-r from-[#1B5AFF] to-[#00C9A7] text-[#0A0F1E]'
                        : 'bg-[#E8EDF5] text-[#0A0F1E]'
                      : item.accent
                        ? 'text-[#1B5AFF] hover:bg-[#1B5AFF]/10'
                        : 'text-[#5B6472] hover:bg-[#E8EDF5] hover:text-[#0A0F1E]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className={`w-5 h-5 ${item.accent && !isActive ? 'text-[#1B5AFF]' : ''}`} />
                  <span className="flex-1 text-sm font-medium">{item.label}</span>
                  {item.badge && (
                    <Badge className="bg-[#1B5AFF] text-[#0A0F1E] border-0 text-xs min-w-[20px] h-5 flex items-center justify-center">
                      {item.badge}
                    </Badge>
                  )}
                  {item.accent && !isActive && (
                    <ChevronRight className="w-4 h-4 opacity-50" />
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-[#E8EDF5]">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#E8EDF5]"
            data-testid="founder-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 min-h-screen">
        {location.pathname === '/founder/profile' ? (
          <div className="p-6">
            <EmployeeProfile isFounderMode={true} />
          </div>
        ) : location.pathname === '/founder/company' ? (
          <div className="p-6">
            <EmployerProfile isFounderMode={true} />
          </div>
        ) : location.pathname === '/founder/messages' ? (
          <div className="p-6">
            <EmployerMessages isFounderMode={true} />
          </div>
        ) : location.pathname === '/founder/search' ? (
          <div className="p-6">
            <EmployerSearch isFounderMode={true} />
          </div>
        ) : (
          // Render main dashboard
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-bold text-[#0A0F1E]">
                  Welcome back, {employeeProfile?.first_name || 'Founder'}
                </h1>
                <p className="text-[#5B6472]">
                  Your personal VETT passport & platform overview
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#E8EDF5]"
                  onClick={() => navigate('/founder/messages')}
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notifications
                  {unreadCount > 0 && (
                    <Badge className="ml-2 bg-[#1B5AFF] text-[#0A0F1E] border-0">{unreadCount}</Badge>
                  )}
                </Button>
                <Button
                  onClick={() => navigate('/admin/command-center')}
                  className="bg-gradient-to-r from-[#1B5AFF] to-[#00C9A7] hover:from-[#D64550] hover:to-[#E85A5A] text-[#0A0F1E]"
                  data-testid="goto-command-center"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Command Center
                </Button>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {/* VETT Score Card */}
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[#5B6472] text-sm">VETT Score</span>
                  <Award className="w-5 h-5 text-[#1B5AFF]" />
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-[#0A0F1E]">
                    {vettScore?.overall_score || '-'}
                  </span>
                  {vettScore?.tier && (
                    <Badge className="bg-[#4CBA84]/20 text-[#4CBA84] border-0 mb-1">
                      {vettScore.tier}
                    </Badge>
                  )}
                </div>
              </Card>

              {/* Records Card */}
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[#5B6472] text-sm">My Records</span>
                  <FileText className="w-5 h-5 text-[#5B8AF0]" />
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-[#0A0F1E]">
                    {employeeRecords.length}
                  </span>
                  <span className="text-[#5B6472] text-sm mb-1">verified</span>
                </div>
              </Card>

              {/* Platform MRR */}
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[#5B6472] text-sm">Platform MRR</span>
                  <TrendingUp className="w-5 h-5 text-[#4CBA84]" />
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-[#0A0F1E]">
                    ${platformStats?.subscriptions?.mrr?.toLocaleString() || '-'}
                  </span>
                </div>
              </Card>

              {/* Total Users */}
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[#5B6472] text-sm">Total Users</span>
                  <Users className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-[#0A0F1E]">
                    {((platformStats?.users?.total_employers || 0) + (platformStats?.users?.total_employees || 0)).toLocaleString()}
                  </span>
                </div>
              </Card>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Personal VETT Passport */}
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-[#1B5AFF]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#1B5AFF]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#0A0F1E]">My VETT Passport</h2>
                    <p className="text-[#5B6472] text-sm">Your verified employment history</p>
                  </div>
                </div>

                {/* Profile Summary */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-[#F5F8FF] rounded-lg">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-[#0A0F1E] text-2xl font-bold">
                      {employeeProfile?.first_name?.charAt(0) || 'F'}
                    </div>
                    <div>
                      <p className="text-[#0A0F1E] font-semibold text-lg">
                        {employeeProfile?.first_name} {employeeProfile?.last_name}
                      </p>
                      <p className="text-[#5B6472]">{employeeProfile?.job_title || 'Founder'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className="bg-[#5B8AF0]/20 text-[#5B8AF0] border-0 text-xs">
                          {user?.vett_id}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-[#F5F8FF] rounded-lg">
                      <p className="text-xl font-bold text-[#0A0F1E]">{employeeRecords.filter(r => r.status === 'published').length}</p>
                      <p className="text-[#5B6472] text-xs">Co-Signed</p>
                    </div>
                    <div className="text-center p-3 bg-[#F5F8FF] rounded-lg">
                      <p className="text-xl font-bold text-[#0A0F1E]">{employeeRecords.filter(r => r.status === 'pending_acknowledgment').length}</p>
                      <p className="text-[#5B6472] text-xs">Pending</p>
                    </div>
                    <div className="text-center p-3 bg-[#F5F8FF] rounded-lg">
                      <p className="text-xl font-bold text-[#0A0F1E]">{vettScore?.verified_years || 0}</p>
                      <p className="text-[#5B6472] text-xs">Years Verified</p>
                    </div>
                  </div>

                  <Button
                    onClick={() => navigate('/founder/profile')}
                    variant="outline"
                    className="w-full border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#E8EDF5]"
                  >
                    Edit Profile
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </Card>

              {/* Platform Overview */}
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#0A0F1E]">Platform Overview</h2>
                    <p className="text-[#5B6472] text-sm">Live platform metrics</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Subscription Breakdown */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 bg-[#F5F8FF] rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#5B6472] text-sm">Employers</span>
                        <Building2 className="w-4 h-4 text-[#5B8AF0]" />
                      </div>
                      <p className="text-2xl font-bold text-[#0A0F1E]">{platformStats?.users?.total_employers || 0}</p>
                    </div>
                    <div className="p-4 bg-[#F5F8FF] rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#5B6472] text-sm">Employees</span>
                        <Users className="w-4 h-4 text-[#4CBA84]" />
                      </div>
                      <p className="text-2xl font-bold text-[#0A0F1E]">{platformStats?.users?.total_employees || 0}</p>
                    </div>
                  </div>

                  {/* Subscription Tiers */}
                  <div className="p-4 bg-[#F5F8FF] rounded-lg">
                    <p className="text-[#5B6472] text-sm mb-3">Active Subscriptions</p>
                    <div className="flex items-center justify-between">
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#0A0F1E]">{platformStats?.subscriptions?.starter || 0}</p>
                        <p className="text-[#5B6472] text-xs">Starter</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#0A0F1E]">{platformStats?.subscriptions?.growth || 0}</p>
                        <p className="text-[#5B6472] text-xs">Growth</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#0A0F1E]">{platformStats?.subscriptions?.pro_employees || 0}</p>
                        <p className="text-[#5B6472] text-xs">Pro Users</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#0A0F1E]">{platformStats?.subscriptions?.active_trials || 0}</p>
                        <p className="text-[#5B6472] text-xs">Trials</p>
                      </div>
                    </div>
                  </div>

                  {/* Records Stats */}
                  <div className="p-4 bg-[#F5F8FF] rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[#5B6472] text-sm">Total VETT Records</p>
                        <p className="text-2xl font-bold text-[#0A0F1E]">{platformStats?.records?.total || 0}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#5B6472] text-sm">Published</p>
                        <p className="text-xl font-bold text-[#4CBA84]">{platformStats?.records?.published || 0}</p>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => navigate('/admin/command-center')}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-[#0A0F1E]"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Open Command Center
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 mt-6">
              <h3 className="text-[#0A0F1E] font-semibold mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button
                  onClick={() => navigate('/founder/search')}
                  variant="outline"
                  className="h-auto flex-col gap-2 p-4 border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#E8EDF5]"
                >
                  <Search className="w-6 h-6 text-[#5B8AF0]" />
                  <span>Talent Search</span>
                </Button>
                <Button
                  onClick={() => navigate('/founder/messages')}
                  variant="outline"
                  className="h-auto flex-col gap-2 p-4 border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#E8EDF5]"
                >
                  <MessageSquare className="w-6 h-6 text-[#4CBA84]" />
                  <span>Messages</span>
                </Button>
                <Button
                  onClick={() => navigate('/admin/users')}
                  variant="outline"
                  className="h-auto flex-col gap-2 p-4 border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#E8EDF5]"
                >
                  <Users className="w-6 h-6 text-purple-400" />
                  <span>Manage Users</span>
                </Button>
                <Button
                  onClick={() => navigate('/admin/financial')}
                  variant="outline"
                  className="h-auto flex-col gap-2 p-4 border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#E8EDF5]"
                >
                  <TrendingUp className="w-6 h-6 text-[#1B5AFF]" />
                  <span>Financials</span>
                </Button>
              </div>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default FounderDashboard;
